import { useEffect, useState } from 'react';
import { Card, SectionLabel } from './ui';
import Info from './Glossary';
import { engineLabel, type ScanRun, type ScanRunDetail } from './ScanHistory';

interface P { query: string; state: string }

/**
 * Where the two engines disagree about the same site, on the same questions.
 *
 * We keep telling clients that Gemini and GPT contradict each other more than
 * people expect, and then make them prove it by flipping a filter back and
 * forth. This puts both readings of the same question side by side.
 *
 * It compares the latest run per engine, and only questions BOTH ran — a
 * question one engine never asked is not a disagreement, and counting it as one
 * would manufacture conflict out of a gap in the data.
 */
const RANK: Record<string, { label: string; cls: string; weight: number }> = {
  mentioned: { label: 'named', cls: 'text-success', weight: 2 },
  cited_only: { label: 'used, not named', cls: 'text-estimated', weight: 1 },
  absent: { label: 'not named', cls: 'text-[#E0574A]', weight: 0 },
};

export default function EngineDisagreement({ comparison, detailFetcher }: {
  comparison: ScanRun[];
  detailFetcher: (id: string) => Promise<ScanRunDetail>;
}): JSX.Element | null {
  const [details, setDetails] = useState<Record<string, ScanRunDetail> | null>(null);

  // Only meaningful with exactly two engines to compare.
  const pair = comparison.filter((r) => r.engine === 'gemini' || r.engine === 'openai');
  const ids = pair.map((r) => r.id).join(',');

  useEffect(() => {
    if (pair.length < 2) { setDetails(null); return; }
    let cancelled = false;
    Promise.all(pair.map((r) => detailFetcher(r.id)))
      .then((ds) => {
        if (cancelled) return;
        setDetails(Object.fromEntries(ds.map((d, i) => [pair[i]!.engine, d])));
      })
      .catch(() => { if (!cancelled) setDetails(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, detailFetcher]);

  if (pair.length < 2 || !details) return null;

  const probesOf = (engine: string): P[] => {
    const d = details[engine];
    if (!d) return [];
    const fp = d.detail.footprint as { probes?: P[] } | null;
    return [...((d.detail.visibility ?? []) as P[]), ...(fp?.probes ?? [])];
  };
  const a = new Map(probesOf('gemini').map((p) => [p.query, p.state]));
  const b = new Map(probesOf('openai').map((p) => [p.query, p.state]));

  const shared = [...a.keys()].filter((q) => b.has(q));
  if (shared.length === 0) return null;

  const rows = shared
    .map((q) => ({ q, g: a.get(q)!, o: b.get(q)! }))
    .filter((r) => r.g !== r.o)
    .sort((x, y) => Math.abs((RANK[y.g]?.weight ?? 0) - (RANK[y.o]?.weight ?? 0))
      - Math.abs((RANK[x.g]?.weight ?? 0) - (RANK[x.o]?.weight ?? 0)));

  const agreePct = Math.round(((shared.length - rows.length) / shared.length) * 100);

  return (
    <>
      <SectionLabel>Where the engines disagree</SectionLabel>
      <Card className="p-4 mt-3 mb-5">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-serif text-lg">Gemini and GPT on the same {shared.length} questions</h3>
          <Info term="engine" />
        </div>
        <p className="text-sm text-text-secondary mb-4">
          They agree on <span className="text-text-primary">{agreePct}%</span> of them. Comparing the latest run of each
          engine, and only questions both actually asked. A question one engine never ran is a gap, not a disagreement.
        </p>

        {rows.length === 0 ? (
          <p className="text-sm text-text-secondary">
            No disagreements in this pair. That is unusual and worth trusting more than a single engine&apos;s reading.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-text-secondary font-mono text-xs text-left">
                <th className="py-1 pr-3">Question</th>
                <th className="pr-3 w-40">{engineLabel('gemini')}</th>
                <th className="w-40">{engineLabel('openai')}</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.q} className="border-t border-warm-dark-2">
                    <td className="py-2 pr-3 text-text-primary">{r.q}</td>
                    <td className={`pr-3 font-mono text-xs ${RANK[r.g]?.cls ?? 'text-text-secondary'}`}>{RANK[r.g]?.label ?? r.g}</td>
                    <td className={`font-mono text-xs ${RANK[r.o]?.cls ?? 'text-text-secondary'}`}>{RANK[r.o]?.label ?? r.o}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="font-mono text-xs text-text-secondary/70 mt-2">
              A question one engine names you for and the other does not is not a bug in either. It is what
              non-deterministic retrieval looks like. Treat a result confirmed by both as the solid one.
            </p>
          </div>
        )}
      </Card>
    </>
  );
}
